/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Text } from '@/shared/ui';
import { FormattedDate } from '@/shared/ui/components/basic/formatted-date';
import { type Deal } from '@/services/requests/housien-services/DealsService';
import { DealStageBadge } from './DealStageBadge';

export interface DealMobileCardProps {
  deal: Deal;
  agencyId: string;
}

/**
 * Compact card for the deals listing on narrow viewports, mirroring the
 * table row's fields [Fase 6, receita canônica].
 */
export function DealMobileCard({ deal, agencyId }: DealMobileCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('features/deals');

  const dealType = deal.deal_type === 'sale' ? t('types.sale') : t('types.rental');
  const formattedValue = deal.reference_value
    ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(deal.reference_value)
    : '—';

  return (
    <div
      data-testid="deal-mobile-card"
      onClick={() => navigate(`/app/agencies/${agencyId}/deals/${deal.deal_id}`)}
      className="flex flex-col gap-(--space-2) rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-primary) p-(--space-4) shadow-[var(--shadow-sm)] cursor-pointer"
    >
      <div className="flex items-start justify-between gap-(--space-2)">
        <Text component="span" className="text-[length:var(--text-body-sm)] [font-weight:var(--font-semibold)] text-(--text-primary) truncate">
          {deal.client_name ?? deal.client_id ?? '—'}
        </Text>
        <DealStageBadge stage={deal.stage} />
      </div>
      <Text component="span" className="text-[length:var(--text-meta)] text-(--text-tertiary) truncate">
        {deal.property_title ?? deal.property_id ?? '—'}
      </Text>
      <div className="flex items-center justify-between gap-(--space-2) text-[length:var(--text-meta)] text-(--text-secondary)">
        <Text component="span">{dealType} · {formattedValue}</Text>
        {deal.updated_at && <FormattedDate value={deal.updated_at} />}
      </div>
    </div>
  );
}

export default DealMobileCard;
